import { useQueryClient } from "@tanstack/react-query";
import { QueryDealActivityArgs } from "../graphql/generated";
import { useParams } from "react-router-dom";

export function useInvalidateQueryKeys() {
  const queryClient = useQueryClient();
  const { dealId: activeDealId } = useParams<{ dealId: string }>();

  function invalidateDealActivities() {
    const activityQueries = queryClient
      .getQueryCache()
      .findAll({ queryKey: ["DealActivity"] });
    for (const query of activityQueries) {
      if (query.queryKey.length > 1) {
        const dealId = (query.queryKey[1] as QueryDealActivityArgs)["dealId"];
        if (dealId === activeDealId) {
          queryClient.invalidateQueries({ queryKey: query.queryKey });
        }
      }
    }
  }

  function invalidatePermissionChecks() {
    const permissionCheckQueries = queryClient
      .getQueryCache()
      .findAll({ queryKey: ["PermissionCheck"] });

    for (const query of permissionCheckQueries) {
      queryClient.invalidateQueries({ queryKey: query.queryKey });
    }
  }

  function invalidateQuestions() {
    const questionQueries = queryClient
      .getQueryCache()
      .findAll({ queryKey: ["Questions"] });
    for (const query of questionQueries) {
      queryClient.invalidateQueries({ queryKey: query.queryKey });
    }

    const dealGroupQuestionsQueries = queryClient
      .getQueryCache()
      .findAll({ queryKey: ["DealGroupQuestions"] });
    for (const query of dealGroupQuestionsQueries) {
      queryClient.invalidateQueries({ queryKey: query.queryKey });
    }
  }

  function invalidateDdql(id: string, dealId: string) {
    const queries = queryClient
      .getQueryCache()
      .findAll({ queryKey: ["Ddql", { dealId, id }] });

    for (const query of queries) {
      queryClient.invalidateQueries({ queryKey: query.queryKey });
    }
  }

  function invalidateAllDdqlQueries() {
    const queries = queryClient.getQueryCache().findAll({ queryKey: ["Ddql"] });

    for (const query of queries) {
      queryClient.invalidateQueries({ queryKey: query.queryKey });
    }
  }

  function invalidateDataRoomFolders() {
    for (const queryKeyPrefix of [
      "DataRoomFolder",
      "DataRoomFolderBrowse",
      "DataRoomFolderPills",
      "AllDataRoomFolders",
      "AllDataRoomFoldersBrowse",
    ]) {
      const folderQueries = queryClient
        .getQueryCache()
        .findAll({ queryKey: [queryKeyPrefix] });
      for (const query of folderQueries) {
        queryClient.invalidateQueries({ queryKey: query.queryKey });
      }
    }
  }

  /** Invalidate list caches for a specific folder id (browse UI + legacy full query). */
  function invalidateDataRoomFolder(folderId: string) {
    if (!folderId) {
      return;
    }
    for (const queryKeyPrefix of [
      "DataRoomFolder",
      "DataRoomFolderBrowse",
      "DataRoomFolderPills",
    ]) {
      queryClient.invalidateQueries({
        queryKey: [queryKeyPrefix, { id: folderId }],
      });
    }
    // Tree view is deal-scoped; invalidate all browse tree caches
    queryClient.invalidateQueries({ queryKey: ["AllDataRoomFoldersBrowse"] });
    queryClient.invalidateQueries({ queryKey: ["AllDataRoomFolders"] });
  }

  /** Invalidate file page caches (shell + tabs + download URLs). */
  function invalidateDataRoomFile(fileId: string) {
    if (!fileId) {
      return;
    }
    for (const queryKeyPrefix of [
      "DataRoomFile",
      "DataRoomFileShell",
      "DataRoomFileVersions",
      "DataRoomFileOverview",
      "DataRoomFileComments",
      "DataRoomFileSettings",
      "DataRoomFileLinkedQuestions",
    ]) {
      queryClient.invalidateQueries({
        queryKey: [queryKeyPrefix, { id: fileId }],
      });
    }
    queryClient.invalidateQueries({ queryKey: ["FileDownloadUrl"] });
    queryClient.invalidateQueries({ queryKey: ["FileVersionDownloadUrl"] });
  }

  function invalidateTransactionFiles() {
    const transactionFileQueries = queryClient
      .getQueryCache()
      .findAll({ queryKey: ["TransactionFiles"] });
    for (const query of transactionFileQueries) {
      queryClient.invalidateQueries({ queryKey: query.queryKey });
    }
  }

  function invalidateDataRooms() {
    const dataRoomQueries = queryClient
      .getQueryCache()
      .findAll({ queryKey: ["DataRoom"] });
    for (const query of dataRoomQueries) {
      if (query.queryKey.length > 1) {
        const dealId = (query.queryKey[1] as QueryDealActivityArgs)["dealId"];
        if (dealId === activeDealId) {
          queryClient.invalidateQueries({ queryKey: query.queryKey });
        }
      }
    }

    for (const queryKeyPrefix of [
      "AllDataRoomFolders",
      "AllDataRoomFoldersBrowse",
    ]) {
      const allDataRoomFoldersQueries = queryClient
        .getQueryCache()
        .findAll({ queryKey: [queryKeyPrefix] });
      for (const query of allDataRoomFoldersQueries) {
        queryClient.invalidateQueries({ queryKey: query.queryKey });
      }
    }
  }

  return {
    invalidateDealActivities,
    invalidateDataRoomFolders,
    invalidateDataRoomFolder,
    invalidateDataRoomFile,
    invalidateDataRooms,
    invalidateTransactionFiles,
    invalidateQuestions,
    invalidateDdql,
    invalidatePermissionChecks,
    invalidateAllDdqlQueries,
  };
}
